'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useCurrentUser, SidebarIcon } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import type { PaddingBox } from '../_shared/resolvePadding';
import { SectionShell } from '../_shared/SectionShell';
import { EmptyState } from '../_shared/EmptyState';

// ─── BFF response shape ────────────────────────────────────────────────────

interface BadgeMetadata {
  id?: string;
  name?: string;
  description?: string;
  iconUrl?: string;
}

interface BadgeResponse {
  id?: string | number;
  elementId?: string;
  metadata?: BadgeMetadata;
  total?: number;
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface FullBadgeProps {
  /** Author-supplied copy overrides. Data always comes from the API. */
  heading?: string;
  /** Design token size key e.g. "xl", "2xl". Maps to var(--font-size-{key}). */
  titleFontSize?: string;
  emptyLabel?: string;

  requireAuth?: 'any' | 'in' | 'out';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  style?: CSSProperties;
}

// ─── Config ─────────────────────────────────────────────────────────────────

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');
const bffUrl = (path: string) => `${BASE}${path}`;

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * FullBadge — a full-width grid of badge cards.
 *
 * Each card shows the badge icon, name, description and how many times the
 * user has earned it (locked badges are shown dimmed). All data comes from
 * the live gamification API. Auth-gated by default.
 */
export function FullBadge({
  heading = 'Rozetlerin',
  titleFontSize,
  emptyLabel = 'Henüz rozet bulunmuyor.',
  requireAuth,
  paddingOuter,
  paddingInner,
  style,
}: FullBadgeProps) {
  const currentUser = useCurrentUser();
  const cls = 'lms-fbadge';

  const [badges, setBadges] = useState<BadgeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const abort = useRef<AbortController | null>(null);

  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';

  useEffect(() => {
    if (!isSignedIn) {
      setLoading(false);
      return;
    }

    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    setLoading(true);

    fetch(bffUrl('/api/gamification/all-badges'), { signal: ctrl.signal })
      .then<BadgeResponse[]>((r) => (r.ok ? r.json() : []))
      .then((bdg) => {
        if (ctrl.signal.aborted) return;
        setBadges(Array.isArray(bdg) ? bdg : []);
        setLoading(false);
      })
      .catch(() => {
        if (ctrl.signal.aborted) return;
        setBadges([]);
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [isSignedIn]);

  if ((gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn)) return null;

  return (
    <SectionShell
      title={heading}
      titleFontSize={titleFontSize}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      style={style}
    >
      <section className={`${cls}-root`}>
        <style dangerouslySetInnerHTML={{ __html: css(cls) }} />

        {loading ? (
          <Skeleton cls={cls} />
        ) : badges.length === 0 ? (
          <EmptyState title={emptyLabel} />
        ) : (
          <div className={`${cls}-grid`}>
            {badges.map((b, i) => (
              <BadgeCard key={b.id ?? b.elementId ?? i} cls={cls} badge={b} />
            ))}
          </div>
        )}
      </section>
    </SectionShell>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function BadgeCard({ cls, badge: b }: { cls: string; badge: BadgeResponse }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [openBelow, setOpenBelow] = useState(false);

  const onEnter = () => {
    const card = cardRef.current;
    const grid = card?.parentElement;
    if (!card || !grid) return;
    // The grid reflows to a different column count per viewport width, so a
    // fixed nth-child rule can't identify "the top row". Compare the card's
    // top edge to the grid's own top edge instead — true only for row 1,
    // regardless of column count. Row-1 cards sit right under the page
    // heading with no headroom, so their popover always opens below;
    // every other row has a card above it and opens above as usual.
    const isFirstRow =
      Math.abs(card.getBoundingClientRect().top - grid.getBoundingClientRect().top) < 1;
    setOpenBelow(isFirstRow);
  };

  const earned = (b.total ?? 0) > 0;
  const name = b.metadata?.name ?? '';
  const description = b.metadata?.description;
  const iconUrl = b.metadata?.iconUrl;

  return (
    <Card asChild>
      <div
        ref={cardRef}
        onMouseEnter={onEnter}
        className={`${cls}-card${earned ? '' : ' locked'}${openBelow ? ` ${cls}-pop-below` : ''}`}
      >
        <span className={`${cls}-count`}>x{b.total ?? 0}</span>
        <div className={`${cls}-media`}>
          {iconUrl ? (
            <img src={iconUrl} alt={name} loading="lazy" />
          ) : (
            <SidebarIcon name="award" size={40} />
          )}
        </div>
        <div className={`${cls}-body`}>
          <div className={`${cls}-name`}>{name}</div>
        </div>
        {description && (
          <div className={`${cls}-pop`} role="tooltip">
            {name && <strong className={`${cls}-pop-title`}>{name}</strong>}
            <span>{description}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

function css(cls: string): string {
  const MUT = 'var(--color-muted-fg,#64748b)';
  const CARD = 'var(--color-card,#fff)';
  const BRAND = 'var(--color-brand-500,#1aa5bc)';

  return `
.${cls}-root{width:100%;margin:0}

.${cls}-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:16px}

/* Rendered as <Card>: border + elevation come from the component. overflow
   must stay visible so the hover description popover can escape the card. */
.${cls}-card{position:relative;overflow:visible;display:flex;flex-direction:column;align-items:center;text-align:center;gap:10px;
  padding:22px 16px 18px;border-radius:16px;background:${CARD};transition:transform .15s ease}
.${cls}-card:hover{transform:translateY(-2px);z-index:10}
.${cls}-card.locked{background:${CARD}}
.${cls}-card.locked .${cls}-media,
.${cls}-card.locked .${cls}-name,
.${cls}-card.locked .${cls}-count{opacity:.5}

.${cls}-media{flex-shrink:0;width:84px;height:84px;border-radius:999px;display:grid;place-items:center;
  color:#fff;overflow:hidden;background:linear-gradient(140deg, ${BRAND}, color-mix(in srgb, ${BRAND} 70%, #000));
  box-shadow:0 6px 16px -8px ${BRAND}}
.${cls}-media img{width:100%;height:100%;object-fit:contain;padding:8px}
.${cls}-card.locked .${cls}-media img{filter:grayscale(1)}
.${cls}-card.locked .${cls}-media{background:${MUT}}

.${cls}-body{min-width:0;width:100%}
.${cls}-name{font-size:14.5px;font-weight:800;color:${BRAND};line-height:1.3}

/* ── Hover popover — full description ─────────────────────────────────── */
.${cls}-pop{position:absolute;left:50%;bottom:calc(100% + 8px);transform:translate(-50%,4px);
  width:max-content;max-width:min(260px,80vw);padding:10px 12px;border-radius:10px;text-align:left;
  background:#0f172a;color:#fff;font-size:12.5px;line-height:1.45;
  box-shadow:0 14px 30px -10px rgba(2,6,23,.55);
  opacity:0;visibility:hidden;pointer-events:none;
  transition:opacity .15s ease, transform .15s ease, visibility .15s ease;z-index:20}
.${cls}-pop-title{display:block;margin-bottom:3px;font-size:13px;font-weight:800}
.${cls}-pop::after{content:'';position:absolute;top:100%;left:50%;transform:translateX(-50%);
  border:6px solid transparent;border-top-color:#0f172a}
.${cls}-card:hover .${cls}-pop{opacity:1;visibility:visible;transform:translate(-50%,0)}

/* ── Auto placement — flips below the card when there's no room above (e.g. top grid row) ── */
.${cls}-card.${cls}-pop-below .${cls}-pop{bottom:auto;top:calc(100% + 8px);transform:translate(-50%,-4px)}
.${cls}-card.${cls}-pop-below:hover .${cls}-pop{transform:translate(-50%,0)}
.${cls}-card.${cls}-pop-below .${cls}-pop::after{top:auto;bottom:100%;border-top-color:transparent;border-bottom-color:#0f172a}

.${cls}-count{position:absolute;top:10px;right:10px;min-width:22px;height:22px;padding:0 6px;border-radius:999px;
  display:grid;place-items:center;font-size:11px;font-weight:800;color:var(--color-text-on-brand,#fff);background:${BRAND};z-index:1}
.${cls}-card.locked .${cls}-count{background:${MUT}}

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:640px){
  .${cls}-grid{grid-template-columns:repeat(2,1fr)}
  .${cls}-card{padding:18px 12px 14px}
  .${cls}-media{width:68px;height:68px}
}
@media (max-width:400px){
  .${cls}-grid{grid-template-columns:1fr}
}

/* ── Skeleton ────────────────────────────────────────────────────────── */
@keyframes ${cls}-pulse{0%,100%{opacity:1}50%{opacity:.5}}
.${cls}-skel{border-radius:16px;background:rgba(148,163,184,.16);animation:${cls}-pulse 1.5s ease-in-out infinite}
`;
}

function Skeleton({ cls }: { cls: string }) {
  return (
    <div className={`${cls}-grid`}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className={`${cls}-skel`} style={{ height: 168 }} />
      ))}
    </div>
  );
}
